import { useState, useEffect, useCallback } from 'react';

/**
 * useInstallPrompt — PWA "Add to Home Screen" hook
 *
 * Captures the browser's `beforeinstallprompt` event and suppresses the
 * default browser UI so we can show our own branded install banner.
 *
 * Usage:
 *   const { canInstall, promptInstall, dismissInstall } = useInstallPrompt();
 *
 * - `canInstall`: true when the browser has fired beforeinstallprompt and the
 *   user hasn't dismissed our banner yet
 * - `promptInstall()`: triggers the native install dialog
 * - `dismissInstall()`: hides the banner and persists the dismissal to
 *   localStorage so it doesn't reappear for 7 days
 */

const DISMISSED_KEY = 'pwa_install_dismissed_until';
const DISMISS_DAYS = 7;

function isDismissed(): boolean {
  try {
    const until = localStorage.getItem(DISMISSED_KEY);
    if (!until) return false;
    return Date.now() < parseInt(until, 10);
  } catch {
    return false;
  }
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // If already dismissed recently, don't show
    if (isDismissed()) return;

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault(); // Suppress the default browser UI
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // If already installed (standalone mode), don't show
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setCanInstall(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setCanInstall(false);
      setDeferredPrompt(null);
    }
    // GA4 tracking
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'pwa_install_prompt', { outcome, timestamp: new Date().toISOString() });
    }
  }, [deferredPrompt]);

  const dismissInstall = useCallback(() => {
    setCanInstall(false);
    setDeferredPrompt(null);
    try {
      const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(DISMISSED_KEY, String(until));
    } catch { /* ok */ }
  }, []);

  return { canInstall, promptInstall, dismissInstall };
}
